import { MEDIA_FRAME_ASPECT_RATIOS } from '../../image-fit';
import type { ContentStyles, LandingTokens, Sx } from '../types';
import { BREAKPOINTS, down } from './breakpoints';
import { wrapSx } from './shared.styles';

// Under the text, the image keeps about the width it had beside it, rather than filling the row.
const STACKED_MEDIA_WIDTH = 'min(100%, 440px)';
const MOBILE_MEDIA_WIDTH = '75%';

const grid: Sx = {
  display: 'grid',
  gap: '72px',
  alignItems: 'center',
  padding: '64px 0',
  [down(BREAKPOINTS.content)]: { gridTemplateColumns: '1fr', gap: '32px', padding: '48px 0' },
  [down(BREAKPOINTS.mobile)]: { padding: '42px 0' },
};

export const contentStyles = (t: LandingTokens): ContentStyles => ({
  // Adjacent content blocks share a rule between them, as in the mockup; the wrap width is the block's own.
  section: {
    ...wrapSx(),
    'section[data-block-type="content"] + section[data-block-type="content"] > &': {
      borderTop: `1px solid ${t.lineSoft}`,
    },
  },
  grid: {
    default: { ...grid, gridTemplateColumns: '1fr 1fr' },
    flip: {
      ...grid,
      gridTemplateColumns: '1fr 1fr',
      '& > :last-child': { order: -1 },
      [down(BREAKPOINTS.content)]: {
        gridTemplateColumns: '1fr',
        gap: '32px',
        padding: '48px 0',
        '& > :last-child': { order: 0 },
      },
    },
    single: { ...grid, gridTemplateColumns: '1fr' },
  },
  kicker: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '11.5px',
    fontWeight: 700,
    letterSpacing: '.18em',
    textTransform: 'uppercase',
    color: t.brandInk,
    marginBottom: '14px',
  },
  kickerIcon: { width: 14, height: 14, flex: 'none', color: t.brand },
  title: {
    fontFamily: t.serif,
    fontWeight: 420,
    fontSize: 'clamp(26px, 3cqi, 34px)',
    lineHeight: 1.15,
    letterSpacing: '-.01em',
    margin: '0 0 14px',
  },
  body: { color: t.muted, fontSize: 16, maxWidth: '52ch', margin: 0, whiteSpace: 'pre-line' },
  // Flat on the block: a transparent icon or a fitted image shows the block's own background.
  // A video brings its own card.
  media: {
    position: 'relative',
    borderRadius: '22px',
    aspectRatio: MEDIA_FRAME_ASPECT_RATIOS.content,
    overflow: 'hidden',
    [down(BREAKPOINTS.content)]: { width: STACKED_MEDIA_WIDTH },
    [down(BREAKPOINTS.mobile)]: { width: MOBILE_MEDIA_WIDTH },
  },
});
